import type {Reminder} from '@/services/reminders/models'
import {
	DEFAULT_PRODUCT_REMINDER_FREQUENCY,
	PRODUCT_REMINDER_FREQUENCY_MONTHS,
} from '@/utilities/constants'

export interface ReminderResponse {
	pagination: {limit: number; offset: number; totalPages: number}
	data: Reminder[]
}

const getTimezoneDate = (date: Date): Date => {
	const tz = date.getTimezoneOffset()
	const hours = date.getHours()
	const mins = date.getMinutes()
	const tzHours = tz / 60
	const tzMins = tz % 60
	if (tz > 0) {
		date.setHours(hours + tzHours, mins + tzMins)
	}
	return date
}

const formatDate = (date: Date): string => {
	const d = getTimezoneDate(new Date(date))

	let month: number | string = d.getMonth() + 1
	let day: number | string = d.getDate()
	const year = d.getFullYear()

	if (month <= 9) month = `0${month}`
	if (day <= 9) day = `0${day}`

	return [month, day, year].join('/')
}

const getMonthsFrequencyFromItem = (item: unknown): number => {
	if (!item || typeof item !== 'object') {
		return Number(DEFAULT_PRODUCT_REMINDER_FREQUENCY)
	}

	const maybe = (item as {marketingBrand?: unknown}).marketingBrand
	if (!maybe || typeof maybe !== 'object') {
		return Number(DEFAULT_PRODUCT_REMINDER_FREQUENCY)
	}

	const idVal = (maybe as {id?: unknown}).id

	// only accept string/number as valid id values
	const brandId = idVal === null ? '' : String(idVal)

	// guarded lookup — ensure the key exists on the map before indexing
	const freqMap = PRODUCT_REMINDER_FREQUENCY_MONTHS
	if (brandId && Object.prototype.hasOwnProperty.call(freqMap, brandId)) {
		return Number(freqMap[brandId])
	}

	return Number(DEFAULT_PRODUCT_REMINDER_FREQUENCY)
}

export const addRecurring = (
	remindersData: ReminderResponse,
	futureMonths: number,
	pastMonths: number
): ReminderResponse => {
	const {data} = remindersData
	const now = new Date()
	data.forEach((result) => {
		result.recurringDates = []
		result.pastDates = []
		result.serestoRefillDate = []
		const originalDate = result.date
			? new Date(result.date)
			: new Date(result.startDate)

		if (
			originalDate > now ||
			originalDate.toLocaleDateString() === now.toLocaleDateString()
		) {
			result.recurringDates.push(formatDate(originalDate))
		}

		const monthsFrequency = getMonthsFrequencyFromItem(result)
		const originalSerestoDate = new Date(result.date)
		originalSerestoDate.setDate(originalSerestoDate.getDate() + 210)
		result.serestoRefillDate.push(formatDate(originalSerestoDate))

		const day = originalDate.getDate()
		let i = monthsFrequency
		while (result.recurringDates.length < futureMonths) {
			const nD = new Date(originalDate)
			if (i === 100) {
				break
			}

			const monthsToAdd = originalDate.getMonth() + Number(i)
			nD.setMonth(monthsToAdd)

			if (nD.getDate() !== day) {
				nD.setDate(0)
			}
			if (nD > now) {
				result.recurringDates.push(formatDate(nD))
			} else {
				result.pastDates.push(formatDate(nD))
			}

			i += monthsFrequency
		}
		if (result.pastDates.length > pastMonths) {
			result.pastDates.splice(0, result.pastDates.length - pastMonths)
		}
		if (
			result.pastDates.length < pastMonths &&
			originalDate < now &&
			originalDate.toLocaleDateString() !== now.toLocaleDateString()
		) {
			result.pastDates.push(formatDate(new Date(originalDate.getTime())))
		}
	})
	return remindersData
}
